/**
 * Attachments — data access for the shared right-rail panel (CLAUDE.md §11
 * "attachments dropzone"; FSD F01/F02 V07, FIN-FR-026).
 *
 * Every call goes through `api()` — the session-guarded client — and the
 * generated `@erpx/api` functions. Three properties this file exists to keep:
 *
 * **The client never builds a storage key.** Upload sends the file plus
 * `{module, entity, entityId}` as multipart; `core/storage` on the server is the
 * only thing that turns those into a key. Download asks the server for a
 * 15-minute presigned GET resolved from the attachment's own row — the client
 * names an attachment id, never a path.
 *
 * **No presigned upload.** By design (server `attachments/doc.go`): a presigned
 * PUT cannot be magic-byte sniffed, so the bytes go through the API, which
 * checks extension, content and size before anything reaches storage. A refusal
 * comes back as problem+json and the panel shows it.
 *
 * **Uploads are idempotent.** Each upload carries its own Idempotency-Key, so a
 * retry after a step-up challenge or a dropped response cannot attach the same
 * file twice.
 */

import {
  attachmentsDelete,
  attachmentsDownload,
  attachmentsList,
  attachmentsSetPortalVisibility,
  attachmentsUpload,
  type AttachmentDTO,
  type DownloadDTO,
} from '@erpx/api'

import { api } from '@/api/client'
import { newIdempotencyKey } from '@/lib/idempotency'

export type { AttachmentDTO, DownloadDTO }

/** The permission codes the routes declare (`core/rbac/catalogue_m0.go`). */
export const ATTACHMENT_PERMISSIONS = {
  read: 'attachment.read',
  upload: 'attachment.upload',
  delete: 'attachment.delete',
} as const

/**
 * What the server will accept (`attachments/handler.go` `uploadAccept`,
 * `uploadMaxSizeMB`). Used only to set the file picker's `accept` hint and to
 * tell the user up front — the server's extension + magic-byte sniff + size
 * check is the control, and its refusal is what the panel shows.
 */
export const ATTACHMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.xlsx,.docx,.csv'
export const ATTACHMENT_MAX_MB = 25

/** The record an attachment hangs off. */
export interface AttachmentTarget {
  module: string
  entity: string
  entityId: number
}

export const ATTACHMENT_KEYS = {
  all: ['attachments'] as const,
  list: (target: AttachmentTarget) =>
    ['attachments', target.module, target.entity, target.entityId] as const,
}

/** §5 caps a page at 200; a runaway cursor must not spin the browser. */
const PAGE_LIMIT = 200
const MAX_PAGES = 20

type ListParams = Parameters<typeof attachmentsList>[1]

/** Every attachment on one record, newest first (the server's order). */
export async function listAttachments(target: AttachmentTarget): Promise<AttachmentDTO[]> {
  const out: AttachmentDTO[] = []
  let cursor: string | undefined
  for (let pageNo = 0; pageNo < MAX_PAGES; pageNo += 1) {
    const query: ListParams = {
      module: target.module,
      entity: target.entity,
      entityId: target.entityId,
      limit: PAGE_LIMIT,
      ...(cursor === undefined ? {} : { cursor }),
    }
    const page = await attachmentsList(api(), query)
    out.push(...page.items)
    if (!page.hasMore || page.nextCursor === null || page.nextCursor === '') break
    cursor = page.nextCursor
  }
  return out
}

/** Upload one file to a record. Multipart, through the session-guarded client. */
export function uploadAttachment(
  target: AttachmentTarget,
  file: File,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<AttachmentDTO> {
  return attachmentsUpload(
    api(),
    file,
    { module: target.module, entity: target.entity, entityId: target.entityId },
    idempotencyKey,
  )
}

/** A short-lived presigned download URL, resolved server-side from the row. */
export function downloadAttachment(id: number): Promise<DownloadDTO> {
  return attachmentsDownload(api(), { id: String(id) })
}

/**
 * Soft-delete an attachment. The row version is always sent: a delete that
 * skipped the optimistic-lock check could remove a file somebody else had just
 * replaced under the same row.
 */
export function deleteAttachment(id: number, rowVersion: number): Promise<void> {
  return attachmentsDelete(api(), { id: String(id) }, { rowVersion })
}

/**
 * Share or withdraw a file for the customer / supplier / contractor portal.
 * Staff uploads default to hidden. The row version is sent so a toggle racing
 * another change answers 409 instead of silently overwriting it.
 */
export function setAttachmentPortalVisibility(
  id: number,
  portalVisible: boolean,
  rowVersion: number,
): Promise<AttachmentDTO> {
  return attachmentsSetPortalVisibility(api(), { id: String(id) }, { portalVisible, rowVersion })
}

/**
 * The attachment entity strings the portal reads files for — exactly the cases
 * of `mustOwnAttachmentEntity` in `server/internal/portal/recordaccess.go`. A
 * staff-uploaded file can only reach a counterparty on these records, so these
 * (and only these) get the "share with portal" control. Keep in step with that
 * switch; the spec pins this list. The `portal.*` buckets are the counterparty's
 * own and staff never attach to them, so they are not listed.
 */
export const PORTAL_FACING_ENTITIES: readonly string[] = [
  'sales.order',
  'crm.case',
  'purchase_orders',
  'project_tasks',
]

/** Whether a record of this entity is one a counterparty's portal reads files from. */
export function isPortalFacingEntity(entity: string): boolean {
  return PORTAL_FACING_ENTITIES.includes(entity)
}

/**
 * Only an absolute http(s) URL is ever opened. The server always returns one;
 * this refuses anything else (a `javascript:` URL, a relative path) rather than
 * trust it blindly.
 */
export function isSafeDownloadUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

/** `1.2 MB`-style size, locale-formatted. Sizes are bytes, not money. */
export function formatBytes(bytes: number, locale: string): string {
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const digits = unit === 0 ? 0 : 1
  const text = new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  }).format(value)
  return `${text} ${units[unit]}`
}
